/**
 * @fileoverview CREATE2 and vanity address utilities for EscapeHub SDK.
 *
 * This module provides framework-agnostic utilities for:
 * - Computing CREATE2 addresses
 * - Building minimal proxy (clone) bytecode
 * - Mining vanity addresses with customizable patterns
 *
 * @module @escapehub/token-creator/create2
 */

import { keccak256, getBytes, concat, getAddress, hexlify, randomBytes } from 'ethers';

// ============================================================================
// Constants
// ============================================================================

/**
 * CREATE2 prefix byte (0xff) as defined in EIP-1014.
 * Used in CREATE2 address computation: keccak256(0xff ++ deployer ++ salt ++ keccak256(initCode))
 *
 * @example
 * ```typescript
 * import { CREATE2_PREFIX } from '@escapehub/token-creator';
 *
 * console.log(CREATE2_PREFIX); // '0xff'
 * ```
 */
export const CREATE2_PREFIX = '0xff';

/**
 * OpenZeppelin Clones minimal proxy bytecode prefix.
 * This is the first part of the EIP-1167 minimal proxy pattern.
 *
 * The full bytecode is: MINIMAL_PROXY_PREFIX + implementation address (20 bytes) + MINIMAL_PROXY_SUFFIX
 *
 * @see https://eips.ethereum.org/EIPS/eip-1167
 */
export const MINIMAL_PROXY_PREFIX = '0x3d602d80600a3d3981f3363d3d373d3d3d363d73';

/**
 * OpenZeppelin Clones minimal proxy bytecode suffix.
 * This is the second part of the EIP-1167 minimal proxy pattern.
 *
 * The full bytecode is: MINIMAL_PROXY_PREFIX + implementation address (20 bytes) + MINIMAL_PROXY_SUFFIX
 *
 * @see https://eips.ethereum.org/EIPS/eip-1167
 */
export const MINIMAL_PROXY_SUFFIX = '0x5af43d82803e903d91602b57fd5bf3';

/**
 * Zero salt (32 bytes of zeros).
 * Commonly used as a default salt when no vanity pattern is required.
 *
 * @example
 * ```typescript
 * import { ZERO_SALT, isZeroSalt } from '@escapehub/token-creator';
 *
 * console.log(ZERO_SALT);
 * // '0x0000000000000000000000000000000000000000000000000000000000000000'
 *
 * console.log(isZeroSalt(ZERO_SALT)); // true
 * ```
 */
export const ZERO_SALT = '0x0000000000000000000000000000000000000000000000000000000000000000';

// ============================================================================
// Types
// ============================================================================

/**
 * Vanity pattern matching mode.
 *
 * - `'prefix'`: Pattern must match the beginning of the address (after 0x)
 * - `'suffix'`: Pattern must match the end of the address
 *
 * @example
 * ```typescript
 * import { VanityPatternMode } from '@escapehub/token-creator';
 *
 * const mode: VanityPatternMode = 'prefix';
 * // Matches addresses like 0xDEAD...
 *
 * const suffixMode: VanityPatternMode = 'suffix';
 * // Matches addresses like 0x...BEEF
 * ```
 */
export type VanityPatternMode = 'prefix' | 'suffix';

/**
 * Result of vanity address mining.
 */
export interface VanityResult {
  /**
   * The salt that produces the vanity address (32 bytes hex string).
   */
  salt: string;

  /**
   * The computed CREATE2 address matching the vanity pattern.
   * Checksummed (EIP-55) format.
   */
  address: string;

  /**
   * Number of attempts taken to find the matching address.
   */
  attempts: number;
}

/**
 * Options for vanity address mining.
 */
export interface VanityMiningOptions {
  /**
   * Hex pattern to match (without 0x prefix).
   * Only hex characters (0-9, a-f, A-F) are allowed.
   *
   * @example 'DEAD', 'cafe', '1234'
   */
  pattern: string;

  /**
   * Where to match the pattern in the address.
   *
   * - `'prefix'`: Match at the start (e.g., 0xDEAD...)
   * - `'suffix'`: Match at the end (e.g., ...BEEF)
   *
   * @default 'prefix'
   */
  mode?: VanityPatternMode;

  /**
   * Whether pattern matching is case-sensitive.
   *
   * When false, 'dead' matches 'DEAD', 'Dead', 'deAD', etc.
   * When true, pattern must match exactly (considering EIP-55 checksum).
   *
   * @default false
   */
  caseSensitive?: boolean;

  /**
   * Maximum number of attempts before giving up.
   * Set to 0 or Infinity for unlimited attempts.
   *
   * @default 1_000_000
   */
  maxAttempts?: number;

  /**
   * Callback invoked periodically during mining to report progress.
   * Useful for updating UI or logging progress.
   *
   * @param attempts - Current number of attempts
   * @param hashRate - Current hashes per second (approximate)
   */
  onProgress?: (attempts: number, hashRate: number) => void;

  /**
   * Starting nonce for salt generation.
   * Allows resuming mining from a previous point.
   *
   * @default 0
   */
  startNonce?: number;
}

// ============================================================================
// Core CREATE2 Functions
// ============================================================================

/**
 * Computes a CREATE2 address from factory, salt, and init code hash.
 *
 * The CREATE2 address is computed as:
 * `keccak256(0xff ++ factoryAddress ++ salt ++ initCodeHash)[12:]`
 *
 * @param factoryAddress - Address of the factory contract deploying via CREATE2
 * @param salt - 32-byte salt value (hex string with 0x prefix)
 * @param initCodeHash - keccak256 hash of the contract init code
 * @returns Checksummed CREATE2 address
 *
 * @example
 * ```typescript
 * import { computeCreate2Address, getMinimalProxyInitCodeHash } from '@escapehub/token-creator';
 *
 * const factory = '0x1234567890123456789012345678901234567890';
 * const implementation = '0xabcdefabcdefabcdefabcdefabcdefabcdefabcd';
 * const salt = '0x0000000000000000000000000000000000000000000000000000000000000001';
 *
 * const initCodeHash = getMinimalProxyInitCodeHash(implementation);
 * const predictedAddress = computeCreate2Address(factory, salt, initCodeHash);
 *
 * console.log(predictedAddress); // '0x...'
 * ```
 */
export function computeCreate2Address(
  factoryAddress: string,
  salt: string,
  initCodeHash: string
): string {
  const normalizedFactory = normalizeAddress(factoryAddress);

  // Ensure salt is 32 bytes
  if (getBytes(salt).length !== 32) {
    throw new Error('Salt must be 32 bytes');
  }

  // Ensure initCodeHash is 32 bytes
  if (getBytes(initCodeHash).length !== 32) {
    throw new Error('Init code hash must be 32 bytes');
  }

  // CREATE2 address = keccak256(0xff ++ factory ++ salt ++ initCodeHash)[12:]
  const data = concat([CREATE2_PREFIX, normalizedFactory, salt, initCodeHash]);
  const hash = keccak256(data);

  // Take last 20 bytes (40 hex chars) of the hash
  const addressHex = '0x' + hash.slice(-40);

  return getAddress(addressHex);
}

/**
 * Builds the minimal proxy (EIP-1167 clone) bytecode for an implementation address.
 *
 * The bytecode follows the OpenZeppelin Clones pattern:
 * `MINIMAL_PROXY_PREFIX + implementation (20 bytes) + MINIMAL_PROXY_SUFFIX`
 *
 * @param implementationAddress - Address of the implementation contract
 * @returns The complete minimal proxy bytecode
 *
 * @example
 * ```typescript
 * import { buildMinimalProxyBytecode } from '@escapehub/token-creator';
 *
 * const implementation = '0xabcdefabcdefabcdefabcdefabcdefabcdefabcd';
 * const bytecode = buildMinimalProxyBytecode(implementation);
 *
 * console.log(bytecode);
 * // '0x3d602d80600a3d3981f3363d3d373d3d3d363d73abcdefabcdefabcdefabcdefabcdefabcdefabcd5af43d82803e903d91602b57fd5bf3'
 * ```
 */
export function buildMinimalProxyBytecode(implementationAddress: string): string {
  const normalized = normalizeAddress(implementationAddress).toLowerCase();
  // Remove 0x prefix from implementation address for concatenation
  const implWithout0x = normalized.slice(2);

  return (
    MINIMAL_PROXY_PREFIX.toLowerCase() +
    implWithout0x +
    MINIMAL_PROXY_SUFFIX.slice(2).toLowerCase()
  );
}

/**
 * Computes the keccak256 hash of the minimal proxy bytecode for an implementation.
 *
 * This is the init code hash needed for CREATE2 address computation when
 * deploying clones via OpenZeppelin's Clones library.
 *
 * @param implementationAddress - Address of the implementation contract
 * @returns keccak256 hash of the minimal proxy bytecode
 *
 * @example
 * ```typescript
 * import { getMinimalProxyInitCodeHash, computeCreate2Address } from '@escapehub/token-creator';
 *
 * const implementation = '0xabcdefabcdefabcdefabcdefabcdefabcdefabcd';
 * const initCodeHash = getMinimalProxyInitCodeHash(implementation);
 *
 * // Use with computeCreate2Address to predict clone addresses
 * const factory = '0x1234567890123456789012345678901234567890';
 * const salt = '0x0000000000000000000000000000000000000000000000000000000000000001';
 * const cloneAddress = computeCreate2Address(factory, salt, initCodeHash);
 * ```
 */
export function getMinimalProxyInitCodeHash(implementationAddress: string): string {
  const bytecode = buildMinimalProxyBytecode(implementationAddress);
  return keccak256(bytecode);
}

// ============================================================================
// Vanity Address Mining
// ============================================================================

/**
 * Checks if an address matches a vanity pattern.
 *
 * @param address - The address to check (with or without 0x prefix)
 * @param pattern - The hex pattern to match (without 0x prefix)
 * @param mode - Whether to match at 'prefix' or 'suffix' of address
 * @param caseSensitive - Whether matching is case-sensitive
 * @returns True if the address matches the pattern
 *
 * @example
 * ```typescript
 * import { matchesVanityPattern } from '@escapehub/token-creator';
 *
 * // Prefix matching (case-insensitive)
 * matchesVanityPattern('0xDEAD1234...', 'dead', 'prefix', false); // true
 * matchesVanityPattern('0xDEAD1234...', 'dead', 'prefix', true);  // false (case mismatch)
 *
 * // Suffix matching
 * matchesVanityPattern('0x...BEEF', 'beef', 'suffix', false); // true
 * ```
 */
export function matchesVanityPattern(
  address: string,
  pattern: string,
  mode: VanityPatternMode = 'prefix',
  caseSensitive: boolean = false
): boolean {
  // Remove 0x prefix if present
  const addressHex = address.startsWith('0x') ? address.slice(2) : address;

  const compareAddress = caseSensitive ? addressHex : addressHex.toLowerCase();
  const comparePattern = caseSensitive ? pattern : pattern.toLowerCase();

  if (mode === 'prefix') {
    return compareAddress.startsWith(comparePattern);
  } else {
    return compareAddress.endsWith(comparePattern);
  }
}

/**
 * Validates that a pattern contains only valid hex characters.
 *
 * @param pattern - The pattern to validate (without 0x prefix)
 * @returns True if pattern contains only hex characters (0-9, a-f, A-F)
 *
 * @example
 * ```typescript
 * import { isValidHexPattern } from '@escapehub/token-creator';
 *
 * isValidHexPattern('DEAD');   // true
 * isValidHexPattern('cafe');   // true
 * isValidHexPattern('1234');   // true
 * isValidHexPattern('GHIJ');   // false (G, H, I, J are not hex)
 * isValidHexPattern('0xDEAD'); // false (0x prefix not allowed)
 * ```
 */
export function isValidHexPattern(pattern: string): boolean {
  return /^[0-9a-fA-F]+$/.test(pattern);
}

/**
 * Generates a random salt, optionally incorporating a nonce.
 *
 * When a nonce is provided, the salt is derived from random bytes XORed
 * with the nonce to ensure uniqueness while maintaining randomness.
 *
 * @param nonce - Optional nonce to incorporate into the salt
 * @returns 32-byte hex string salt
 *
 * @example
 * ```typescript
 * import { generateRandomSalt } from '@escapehub/token-creator';
 *
 * // Fully random salt
 * const salt1 = generateRandomSalt();
 *
 * // Salt with nonce (for sequential mining)
 * const salt2 = generateRandomSalt(12345);
 * ```
 */
export function generateRandomSalt(nonce?: number): string {
  const random = randomBytes(32);

  if (nonce !== undefined) {
    // Incorporate nonce into the last 8 bytes
    const nonceBytes = new Uint8Array(8);
    const view = new DataView(nonceBytes.buffer);
    view.setBigUint64(0, BigInt(nonce), false); // big-endian

    const randomArray = new Uint8Array(random);
    for (let i = 0; i < 8; i++) {
      randomArray[24 + i] ^= nonceBytes[i];
    }
    return hexlify(randomArray);
  }

  return hexlify(random);
}

/**
 * Mines a vanity address by trying different salts until a match is found.
 *
 * This is a synchronous operation that will block the event loop.
 * For non-blocking mining, use {@link generateSaltAsync}.
 *
 * @param factoryAddress - Address of the CREATE2 factory contract
 * @param initCodeHash - keccak256 hash of the contract init code
 * @param options - Vanity mining options
 * @returns The salt and address that match the pattern, or null if max attempts reached
 *
 * @throws Error if pattern is invalid
 *
 * @example
 * ```typescript
 * import { generateSalt, getMinimalProxyInitCodeHash } from '@escapehub/token-creator';
 *
 * const factory = '0x1234567890123456789012345678901234567890';
 * const implementation = '0xabcdefabcdefabcdefabcdefabcdefabcdefabcd';
 * const initCodeHash = getMinimalProxyInitCodeHash(implementation);
 *
 * const result = generateSalt(factory, initCodeHash, {
 *   pattern: 'DEAD',
 *   mode: 'prefix',
 *   maxAttempts: 100_000,
 *   onProgress: (attempts, hashRate) => {
 *     console.log(`Attempts: ${attempts}, Rate: ${hashRate} H/s`);
 *   }
 * });
 *
 * if (result) {
 *   console.log(`Found! Address: ${result.address}, Salt: ${result.salt}`);
 *   console.log(`Took ${result.attempts} attempts`);
 * } else {
 *   console.log('No match found within max attempts');
 * }
 * ```
 */
export function generateSalt(
  factoryAddress: string,
  initCodeHash: string,
  options: VanityMiningOptions
): VanityResult | null {
  const {
    pattern,
    mode = 'prefix',
    caseSensitive = false,
    maxAttempts = 1_000_000,
    onProgress,
    startNonce = 0,
  } = options;

  // Validate pattern
  if (!isValidHexPattern(pattern)) {
    throw new Error(`Invalid hex pattern: ${pattern}. Only hex characters (0-9, a-f, A-F) allowed.`);
  }

  if (pattern.length > 40) {
    throw new Error(`Pattern too long: ${pattern.length} chars. Maximum is 40 (full address).`);
  }

  const normalizedFactory = normalizeAddress(factoryAddress);
  const limit = maxAttempts === 0 ? Infinity : maxAttempts;
  const progressInterval = 10_000; // Report every 10k attempts
  let lastProgressTime = Date.now();
  let lastProgressAttempts = 0;

  for (let nonce = startNonce; nonce - startNonce < limit; nonce++) {
    const salt = generateRandomSalt(nonce);
    const address = computeCreate2Address(normalizedFactory, salt, initCodeHash);

    if (matchesVanityPattern(address, pattern, mode, caseSensitive)) {
      return {
        salt,
        address,
        attempts: nonce - startNonce + 1,
      };
    }

    // Progress callback
    if (onProgress && (nonce - startNonce + 1) % progressInterval === 0) {
      const now = Date.now();
      const elapsed = (now - lastProgressTime) / 1000;
      const attemptsDone = nonce - startNonce + 1 - lastProgressAttempts;
      const hashRate = Math.round(attemptsDone / elapsed);

      onProgress(nonce - startNonce + 1, hashRate);

      lastProgressTime = now;
      lastProgressAttempts = nonce - startNonce + 1;
    }
  }

  return null;
}

/**
 * Mines a vanity address asynchronously, yielding to the event loop periodically.
 *
 * This allows other operations to proceed during mining, making it suitable
 * for use in web applications or Node.js servers.
 *
 * @param factoryAddress - Address of the CREATE2 factory contract
 * @param initCodeHash - keccak256 hash of the contract init code
 * @param options - Vanity mining options
 * @param yieldInterval - Number of attempts between event loop yields
 * @returns Promise resolving to the salt and address, or null if max attempts reached
 *
 * @example
 * ```typescript
 * import { generateSaltAsync, getMinimalProxyInitCodeHash } from '@escapehub/token-creator';
 *
 * const factory = '0x1234567890123456789012345678901234567890';
 * const implementation = '0xabcdefabcdefabcdefabcdefabcdefabcdefabcd';
 * const initCodeHash = getMinimalProxyInitCodeHash(implementation);
 *
 * const result = await generateSaltAsync(factory, initCodeHash, {
 *   pattern: 'CAFE',
 *   mode: 'prefix',
 *   maxAttempts: 500_000,
 *   onProgress: (attempts, hashRate) => {
 *     console.log(`Progress: ${attempts} attempts, ${hashRate} H/s`);
 *   }
 * }, 1000); // Yield every 1000 attempts
 *
 * if (result) {
 *   console.log(`Found vanity address: ${result.address}`);
 * }
 * ```
 */
export async function generateSaltAsync(
  factoryAddress: string,
  initCodeHash: string,
  options: VanityMiningOptions,
  yieldInterval: number = 1000
): Promise<VanityResult | null> {
  const {
    pattern,
    mode = 'prefix',
    caseSensitive = false,
    maxAttempts = 1_000_000,
    onProgress,
    startNonce = 0,
  } = options;

  // Validate pattern
  if (!isValidHexPattern(pattern)) {
    throw new Error(`Invalid hex pattern: ${pattern}. Only hex characters (0-9, a-f, A-F) allowed.`);
  }

  if (pattern.length > 40) {
    throw new Error(`Pattern too long: ${pattern.length} chars. Maximum is 40 (full address).`);
  }

  const normalizedFactory = normalizeAddress(factoryAddress);
  const limit = maxAttempts === 0 ? Infinity : maxAttempts;
  const progressInterval = 10_000;
  let lastProgressTime = Date.now();
  let lastProgressAttempts = 0;

  for (let nonce = startNonce; nonce - startNonce < limit; nonce++) {
    const salt = generateRandomSalt(nonce);
    const address = computeCreate2Address(normalizedFactory, salt, initCodeHash);

    if (matchesVanityPattern(address, pattern, mode, caseSensitive)) {
      return {
        salt,
        address,
        attempts: nonce - startNonce + 1,
      };
    }

    // Yield to event loop
    if ((nonce - startNonce + 1) % yieldInterval === 0) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }

    // Progress callback
    if (onProgress && (nonce - startNonce + 1) % progressInterval === 0) {
      const now = Date.now();
      const elapsed = (now - lastProgressTime) / 1000;
      const attemptsDone = nonce - startNonce + 1 - lastProgressAttempts;
      const hashRate = Math.round(attemptsDone / elapsed);

      onProgress(nonce - startNonce + 1, hashRate);

      lastProgressTime = now;
      lastProgressAttempts = nonce - startNonce + 1;
    }
  }

  return null;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Normalizes an Ethereum address to checksummed format.
 *
 * @param address - The address to normalize
 * @returns Checksummed address (EIP-55)
 * @throws Error if address is invalid
 *
 * @example
 * ```typescript
 * import { normalizeAddress } from '@escapehub/token-creator';
 *
 * normalizeAddress('0xdeadbeefdeadbeefdeadbeefdeadbeefdeadbeef');
 * // '0xDeaDbeefdEAdbeefdEadbEEFdeadbeEFdEaDbeeF' (checksummed)
 * ```
 */
export function normalizeAddress(address: string): string {
  return getAddress(address);
}

/**
 * Checks if a salt is the zero salt (all zeros).
 *
 * @param salt - The salt to check
 * @returns True if salt is all zeros
 *
 * @example
 * ```typescript
 * import { isZeroSalt, ZERO_SALT, generateRandomSalt } from '@escapehub/token-creator';
 *
 * isZeroSalt(ZERO_SALT);           // true
 * isZeroSalt(generateRandomSalt()); // false (almost certainly)
 * ```
 */
export function isZeroSalt(salt: string): boolean {
  const bytes = getBytes(salt);
  if (bytes.length !== 32) return false;
  return bytes.every((b) => b === 0);
}

/**
 * Estimates the expected number of attempts to find a vanity address.
 *
 * For prefix matching: 16^patternLength attempts on average
 * For case-sensitive matching with checksummed addresses, the calculation
 * is more complex due to EIP-55 checksum rules.
 *
 * @param patternLength - Length of the vanity pattern
 * @param caseSensitive - Whether matching is case-sensitive
 * @returns Estimated number of attempts
 *
 * @example
 * ```typescript
 * import { estimateVanityAttempts, formatAttempts } from '@escapehub/token-creator';
 *
 * // 4-character prefix
 * const attempts = estimateVanityAttempts(4, false);
 * console.log(formatAttempts(attempts)); // '65,536'
 *
 * // 6-character prefix (harder)
 * const harder = estimateVanityAttempts(6, false);
 * console.log(formatAttempts(harder)); // '16,777,216'
 * ```
 */
export function estimateVanityAttempts(patternLength: number, caseSensitive: boolean = false): number {
  if (patternLength <= 0) return 1;
  if (patternLength > 40) return Infinity;

  // For case-insensitive: 16^n (each hex position has 16 possibilities)
  // For case-sensitive: More complex due to checksum, but approximately 22^n
  // (letters can be upper/lower based on checksum, numbers are always the same)
  const base = caseSensitive ? 22 : 16;
  return Math.pow(base, patternLength);
}

/**
 * Formats a large number with thousand separators.
 *
 * @param attempts - The number to format
 * @returns Formatted string with commas
 *
 * @example
 * ```typescript
 * import { formatAttempts } from '@escapehub/token-creator';
 *
 * formatAttempts(1000);      // '1,000'
 * formatAttempts(1000000);   // '1,000,000'
 * formatAttempts(16777216);  // '16,777,216'
 * ```
 */
export function formatAttempts(attempts: number): string {
  if (!Number.isFinite(attempts)) return '∞';
  return attempts.toLocaleString('en-US');
}

/**
 * Estimates the time to find a vanity address given a hash rate.
 *
 * @param patternLength - Length of the vanity pattern
 * @param hashRate - Hashes per second (attempts per second)
 * @param caseSensitive - Whether matching is case-sensitive
 * @returns Estimated time in seconds
 *
 * @example
 * ```typescript
 * import { estimateVanityTime, formatDuration } from '@escapehub/token-creator';
 *
 * // 4-character pattern at 10,000 H/s
 * const seconds = estimateVanityTime(4, 10_000, false);
 * console.log(formatDuration(seconds)); // '~7 seconds'
 *
 * // 6-character pattern at 10,000 H/s
 * const harder = estimateVanityTime(6, 10_000, false);
 * console.log(formatDuration(harder)); // '~28 minutes'
 * ```
 */
export function estimateVanityTime(
  patternLength: number,
  hashRate: number,
  caseSensitive: boolean = false
): number {
  if (hashRate <= 0) return Infinity;
  const attempts = estimateVanityAttempts(patternLength, caseSensitive);
  return attempts / hashRate;
}

/**
 * Formats a duration in seconds to a human-readable string.
 *
 * @param seconds - Duration in seconds
 * @returns Human-readable duration string
 *
 * @example
 * ```typescript
 * import { formatDuration } from '@escapehub/token-creator';
 *
 * formatDuration(5);         // '~5 seconds'
 * formatDuration(90);        // '~2 minutes'
 * formatDuration(3700);      // '~1 hour'
 * formatDuration(90000);     // '~1 day'
 * formatDuration(1000000);   // '~12 days'
 * formatDuration(Infinity);  // '∞'
 * ```
 */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds)) return '∞';
  if (seconds < 0) return '0 seconds';

  if (seconds < 60) {
    return `~${Math.round(seconds)} second${Math.round(seconds) !== 1 ? 's' : ''}`;
  }

  const minutes = seconds / 60;
  if (minutes < 60) {
    const rounded = Math.round(minutes);
    return `~${rounded} minute${rounded !== 1 ? 's' : ''}`;
  }

  const hours = minutes / 60;
  if (hours < 24) {
    const rounded = Math.round(hours);
    return `~${rounded} hour${rounded !== 1 ? 's' : ''}`;
  }

  const days = hours / 24;
  if (days < 365) {
    const rounded = Math.round(days);
    return `~${rounded} day${rounded !== 1 ? 's' : ''}`;
  }

  const years = days / 365;
  const rounded = Math.round(years * 10) / 10;
  return `~${rounded} year${rounded !== 1 ? 's' : ''}`;
}
